import axios, { AxiosError, AxiosResponse, CancelTokenSource } from 'axios'

import {
  ApiResponse,
  IBookmarksType,
  IPaginationFilter,
  IRefDataDTO,
  IRFAInfoSubCategoryDTO,
  ISubIndustryRefDataDTO,
} from '../models/Common'
import {
  EnquiryPreLodgementRequest,
  EnquiryTellUsMoreStepOneRequest,
  EnquiryTellUsMoreStepTwoRequest,
  EnquiryTellUsMoreSubmitRequest,
  ICurrentUserProfileUpdated,
  IEnquiryDetail,
  IEnquiryPreLodgementResponse,
  IEnquirySaveResponse,
  IEnquirySummary,
  EnquiryGovCMSSearchRequest,
  IEnquiryGovCMSSearchResponse,
  RequestInformationRequest,
  CurrentUserProfileRequest,
  CancelOrCloseRequest,
} from '../models/Enquiry'
import {
  IUserProfile,
  IUserProfileUpdated,
  UserProfile,
  UserProfileRequest,
} from '../models/Profile'
import Resources from './Resources'
import {
  AuthenticationService,
  msalInstance,
  SignInRequiredError,
} from '../services/AuthenticationService'
import { IAbrAbn, IAbrMessage } from '../models/AbrMessage'
import { IHeartbeatResponse } from '../models/HealthMessage'
import { ILocation } from '../models/Address'
import { ITranscriptDetail, ITranscriptInfo } from '../models/Transcript'
import { INewsAlertsResponse } from '../models/NewsAlert'
import {
  BookmarksDeleteRequest,
  BookmarksRequest,
  IBookmarksResponse,
} from '../models/Bookmarks'
import { IPasswordUpdated } from '../models/Password'
import { ICalloutResponse } from '../models/Callout'
import { INewUserEmail } from '../models/ChangeUserEmail'
import {
  UpdateUserAbnRequest,
  UpdateEnquiryAbnRequest,
} from '../models/ChnageABN'
import {
  InitiateMFAPhoneChange,
  InitiateMFAPhoneChangeResponse,
  NewMFAPhone,
} from '../models/ChangeMFAPhone'
import { UserDisable } from '../models/Disable'

const ReturnOriginalRequest = process.env.TestExecutingContext || false //SEE setupTest.ts
const authService: AuthenticationService = ReturnOriginalRequest
  ? null
  : new AuthenticationService(msalInstance)

axios.defaults.baseURL = process.env.REACT_APP_API_URL
axios.defaults.headers = {
  'Access-Control-Allow-Origin': process.env.REACT_APP_API_URL,
  'Access-Control-Allow-Methods': 'GET, POST, PUT, PATCH',
}

axios.interceptors.request.use(
  async (config) => {
    const originalRequest = config
    if (ReturnOriginalRequest) {
      return originalRequest
    }

    return await authService
      .getAccessToken()
      .then((accessToken) => {
        config.headers.Authorization = 'Bearer ' + accessToken
        return Promise.resolve(originalRequest)
      })
      .catch((error) => {
        /*
      MSAL redirect which will do a browser redirect to acquire token. 
      Any promises will not be executed.
    */
        if (error instanceof SignInRequiredError) {
          authService.redirect()
        }

        return Promise.reject(error)
      })
  },
  (error) => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError) => {
    if (error.response) {
      const code = error.response.status
      switch (code) {
        case 400:
        case 404:
          const { status }: { status: number } = error.response.data
          return Promise.reject({ errors: error.response.data?.errors, status })
        case 500:
          return Promise.reject(error.message)
      }
      return Promise.reject(error.response)
    }

    if (error.message === 'Network Error') {
      window.location.href = '/network-error'
      return Promise.reject(
        'There are some connectivity issues. Please try again.'
      )
    }

    return Promise.reject(error.message)
  }
)

const responseBody = <T>(response: AxiosResponse<T>) => response.data

const requests = {
  get: <T>(url: string, source?: CancelTokenSource) =>
    axios.get<T>(url, { cancelToken: source?.token }).then(responseBody),
  post: <T>(url: string, body: null, source?: CancelTokenSource) =>
    axios.post<T>(url, body, { cancelToken: source?.token }).then(responseBody),
  put: <T>(url: string, body: null) =>
    axios.put<T>(url, body).then(responseBody),
  patch: <T>(url: string, body: null) =>
    axios.patch<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
}

const Heartbeat = {
  getHeartbeat: () =>
    requests.get<ApiResponse<IHeartbeatResponse>>(
      `${Resources.HeartbeatEndpoints.Heartbeat}`
    ),
}

const Profiles = {
  getCurrentUserProfile: (source: CancelTokenSource) =>
    requests.get<ApiResponse<UserProfile>>(
      `${Resources.ProfilesEndpoints.CurrentUser}`,
      source
    ),
  getUpdateUserViewModel: (source: CancelTokenSource) =>
    requests.get<ApiResponse<UserProfile>>(
      `${Resources.ProfilesEndpoints.GetUpdateUserViewModel}`,
      source
    ),
  createUserProfile: (userProfile) =>
    requests.post<ApiResponse<IUserProfile>>(
      `${Resources.ProfilesEndpoints.RegisterUser}`,
      userProfile
    ),
  updateUserProfile: (userProfile: UserProfileRequest) =>
    requests.patch<ApiResponse<IUserProfileUpdated>>(
      `${Resources.ProfilesEndpoints.Patch}`,
      userProfile
    ),
  updateCurrentUserProfile: (userProfile: CurrentUserProfileRequest) =>
    requests.patch<ApiResponse<ICurrentUserProfileUpdated>>(
      `${Resources.ProfilesEndpoints.Patch}`,
      userProfile
    ),
  resetPin: (pin) =>
    requests.patch<ApiResponse<IUserProfile>>(
      `${Resources.ProfilesEndpoints.ResetPin}`,
      pin
    ),
  disableUser: (disable: UserDisable) =>
    requests.patch<ApiResponse<IUserProfile>>(
      `${Resources.ProfilesEndpoints.DisableUser}`,
      disable
    ),
  resetPassword: (passwords) =>
    requests.patch<ApiResponse<IUserProfile>>(
      `${Resources.ProfilesEndpoints.ResetPassword}`,
      passwords
    ),
  passwordUpdated: (passwordUpdated: IPasswordUpdated) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.PasswordUpdated}`,
      passwordUpdated
    ),

  changeUserEmail: (newEmail: INewUserEmail) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.ChangeUserEmailAddress}`,
      newEmail
    ),

  changeABN: (newABN: UpdateUserAbnRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.ChangeABN}`,
      newABN
    ),
  changeEmailAddress: (source: CancelTokenSource) =>
    requests.get<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.ChangeEmailAddress}`,
      source
    ),
  changeMFAPhone: (newMFAPhone: NewMFAPhone) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.ChangeMFAPhone}`,
      newMFAPhone
    ),
  initiateMFAPhoneChange: (initiate: InitiateMFAPhoneChange) =>
    requests.patch<ApiResponse<InitiateMFAPhoneChangeResponse>>(
      `${Resources.ProfilesEndpoints.InitiateMFAPhoneChange}`,
      initiate
    ),
  changePhoneNumber: (source: CancelTokenSource) =>
    requests.get<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.ChangePhoneNumber}`,
      source
    ),
  recordLoginDate: (source: CancelTokenSource) =>
    requests.post<ApiResponse<boolean>>(
      `${Resources.ProfilesEndpoints.Logon}`,
      source
    ),
}

const UserRegistrations = {
  getCurrentUserProfile: (source: CancelTokenSource) =>
    requests.get<ApiResponse<UserProfile>>(
      `${Resources.ProfilesEndpoints.CurrentUser}`,
      source
    ),
  getRegisterUserViewModel: (source: CancelTokenSource) =>
    requests.get<ApiResponse<UserProfile>>(
      `${Resources.ProfilesEndpoints.GetRegisterUserViewModel}`,
      source
    ),
}

const Enquiries = {
  getAll: (filter: IPaginationFilter, source: CancelTokenSource) =>
    requests.get<ApiResponse<IEnquirySummary[]>>(
      `${Resources.EnquiriesEndpoints.All}?offset=${filter.offset}&size=${filter.size}`,
      source
    ),
  create: (request: EnquiryPreLodgementRequest) =>
    requests.post<ApiResponse<IEnquiryPreLodgementResponse>>(
      `${Resources.EnquiriesEndpoints.Create}`,
      request
    ),
  getEnquiryDetails: (id: string) =>
    requests.get<ApiResponse<IEnquiryDetail>>(
      `${Resources.EnquiriesEndpoints.GetEnquiryDetails}/${id}`
    ),
  getUpdateEnquiryStep1ViewModel: (id: string) =>
    requests.get<ApiResponse<IEnquiryDetail>>(
      `${Resources.EnquiriesEndpoints.GetUpdateEnquiryStep1ViewModel}?id=${id}`
    ),
  getUpdateEnquiryStep2ViewModel: (id: string) =>
    requests.get<ApiResponse<IEnquiryDetail>>(
      `${Resources.EnquiriesEndpoints.GetUpdateEnquiryStep2ViewModel}?id=${id}`
    ),
  saveStepOne: (request: EnquiryTellUsMoreStepOneRequest) =>
    requests.patch<ApiResponse<IEnquirySaveResponse>>(
      `${Resources.EnquiriesEndpoints.SaveStepOne}`,
      request
    ),
  saveStepTwo: (request: EnquiryTellUsMoreStepTwoRequest) =>
    requests.patch<ApiResponse<IEnquirySaveResponse>>(
      `${Resources.EnquiriesEndpoints.SaveStepTwo}`,
      request
    ),
  submit: (request: EnquiryTellUsMoreSubmitRequest) =>
    requests.patch<ApiResponse<IEnquirySaveResponse>>(
      `${Resources.EnquiriesEndpoints.Submit}`,
      request
    ),
  updateRequestInformation: (request: RequestInformationRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.EnquiriesEndpoints.UpdateRequestInformation}`,
      request
    ),
  cancelEnquiry: (request: CancelOrCloseRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.EnquiriesEndpoints.Cancel}`,
      request
    ),
  closeEnquiry: (request: CancelOrCloseRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.EnquiriesEndpoints.Close}`,
      request
    ),
  changeABN: (newABN: UpdateEnquiryAbnRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.EnquiriesEndpoints.ChangeABN}`,
      newABN
    ),
}

const AbrSearch = {
  getAbnDetails: (id: string) =>
    requests.get<ApiResponse<IAbrAbn>>(
      `${Resources.AbrEndpoints.GetAbnDetails}?id=${id}`
    ),
  getAbnMatchingNames: (name: string) =>
    requests.get<ApiResponse<IAbrMessage>>(
      `${Resources.AbrEndpoints.GetAbnMatchingNames}?name=${name}`
    ),
}

const RefData = {
  getTitles: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(Resources.RefDataEndpoints.Titles),
  getGenders: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Genders
    ),
  getCustomerTypes: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.CustomerTypes
    ),
  getAwards: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(Resources.RefDataEndpoints.Awards),
  getIndustries: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Industries
    ),
  getSubIndustries: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.SubIndustries
    ),
  getSubIndustry: (id: string, source?: CancelTokenSource) =>
    requests.get<ApiResponse<ISubIndustryRefDataDTO>>(
      `${Resources.RefDataEndpoints.SubIndustry}`.replace('{id}', `${id}`),
      source
    ),
  getRegions: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Regions
    ),
  getBusinessDescriptions: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.BusinessDescriptions
    ),
  getYesNoUnsure: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.YesNoUnsure
    ),
  getRequestForAssistanceInfoCategories: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.RequestForAssistanceInfoCategories
    ),
  getRequestForAssistanceInfoSubCategories: () =>
    requests.get<ApiResponse<IRFAInfoSubCategoryDTO[]>>(
      Resources.RefDataEndpoints.RequestForAssistanceInfoSubCategories
    ),
  getIssues: (languageId: number) =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Issues + languageId
    ),

  getSubIssues: (index: number) =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.SubIssues + index
    ),
  getEmploymentAttributes: (languageId: number) =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.EmploymentAttributes + languageId
    ),
  getReportIndustries: (languageId: number) =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.ReportIndustries + languageId
    ),
  getReportSubIndustries: (index: number) =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.ReportSubIndustries + index
    ),
  getEmploymentTypes: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.EmploymentTypes
    ),
  getApprenticeTrainees: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.ApprenticeTrainee
    ),
  getEmployeeDescriptions: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.EmployeeDescriptions
    ),
  getVisaTypes: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.VisaTypes
    ),
  getOfficeLocations: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.OfficeLocations
    ),
  getHowDidYouFind: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.HowDidYouFind
    ),
  getLanguages: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Languages
    ),
  getGenuineContractEmployees: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.GenuineContractEmployees
    ),
  getCountries: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Countries
    ),

  getCountryCodes: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.CountryCodes
    ),
  getOccupations: (query: string) =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      `${Resources.RefDataEndpoints.Occupations}?term=${query}`
    ),
  getReasonNotSpokenToEmployers: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.ReasonNotSpokenToEmployers
    ),
  getEvidences: () =>
    requests.get<ApiResponse<IRefDataDTO[]>>(
      Resources.RefDataEndpoints.Evidences
    ),
}

const Address = {
  getLocations: (query: string) =>
    requests.get<ApiResponse<ILocation[]>>(
      `${Resources.AddressEndpoints.GetLocations}?term=${query}`
    ),
}

const Transcripts = {
  getTranscripts: (id: string) =>
    requests.get<ApiResponse<ITranscriptDetail[]>>(
      `${Resources.TranscriptEndpoints.GetTranscript}?enquiryId=${id}`
    ),
  getTranscript: (id: string) =>
    requests.get<ApiResponse<ITranscriptInfo>>(
      `${Resources.TranscriptEndpoints.GetTranscript}/${id}`
    ),
}

const GovCMS = {
  getGovCMSPages: (request: EnquiryGovCMSSearchRequest) =>
    requests.post<ApiResponse<IEnquiryGovCMSSearchResponse[]>>(
      `${Resources.GovCMSEndpoints.GetRecomendedCMSPages}`,
      request
    ),
  getGovCMSNewsAlerts: (source: CancelTokenSource) =>
    requests.get<ApiResponse<INewsAlertsResponse[]>>(
      `${Resources.GovCMSEndpoints.GetNewsAlertsCMSPages}`,
      source
    ),
}
const Bookmarks = {
  getBookmarks: (
    filter: IPaginationFilter,
    bookmarktype: IBookmarksType,
    source: CancelTokenSource
  ) =>
    requests.get<ApiResponse<IBookmarksResponse>>(
      `${Resources.BookmarksEndpoints.GetBookmarks}?offset=${filter.offset}&size=${filter.size}&types=${bookmarktype.type}`,
      source
    ),
  removeBookmark: (request: BookmarksDeleteRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.BookmarksEndpoints.RemoveBookmark}`,
      request
    ),
  createBookmark: (request: BookmarksRequest) =>
    requests.patch<ApiResponse<boolean>>(
      `${Resources.BookmarksEndpoints.CreateBookmark}`,
      request
    ),
}
const Callout = {
  getCalloutByPage: (page: string, source: CancelTokenSource) =>
    requests.get<ICalloutResponse>(
      `${Resources.CalloutEndpoints.GetCalloutByPage}`.replace(
        '{page}',
        `${page}`
      ),
      source
    ),
}
const Agent = {
  Heartbeat,
  Profiles,
  Enquiries,
  AbrSearch,
  RefData,
  Address,
  Transcripts,
  GovCMS,
  Bookmarks,
  UserRegistrations,
  Callout,
}

export default Agent
